import dayjs from 'dayjs';
import type { AnalyticsDialogsRequest, IntervalData } from '@shared/api';
import type { FilterOption } from '@shared/lib/types';
import type { DateFilterValue } from '@shared/ui/vanilla-calendar/lib/types';
import { Period } from '@shared/ui/vanilla-calendar/lib/types';
import { normalizeOptions } from '@shared/ui/filters/ui/filters/utils/normalize-options';

export const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

export type MetricKey = 'messages' | 'clients' | 'leads';

export type ChartPoint = {
  name: string;
  value: number;
};

export const formatTick = (iso: string, interval: AnalyticsDialogsRequest['interval']) => {
  const d = dayjs(iso);
  return interval === 'hour' ? d.format('HH:00') : d.format('DD.MM');
};

export const mapIntervalsToChartData = (
  intervals: IntervalData[],
  interval: AnalyticsDialogsRequest['interval'],
): ChartPoint[] => intervals.map((p) => ({ name: formatTick(p.date, interval), value: p.value }));

export const normalizeOptionsToNumber = (option?: unknown): FilterOption<number>[] => {
  const normalized = normalizeOptions(option) as FilterOption<string>[];

  return normalized
    .map((opt) => ({ ...opt, value: Number(opt.value) }))
    .filter((opt) => Number.isFinite(opt.value));
};

export const buildDefaultToday = (label: string): FilterOption<DateFilterValue>[] => {
  const today = dayjs();
  const startDate = today.startOf('day').format(DATE_FORMAT);
  const endDate = today.endOf('day').format(DATE_FORMAT);

  return [
    {
      label,
      value: {
        period: Period.CUSTOM,
        startDate,
        endDate,
      },
    },
  ];
};

export const buildDefaultYesterday = (label: string): FilterOption<DateFilterValue>[] => {
  const yesterday = dayjs().subtract(1, 'day');
  const startDate = yesterday.startOf('day').format(DATE_FORMAT);
  const endDate = yesterday.endOf('day').format(DATE_FORMAT);

  return [
    {
      label,
      value: {
        period: Period.YESTERDAY,
        startDate,
        endDate,
      },
    },
  ];
};

export const buildDefaultWeek = (label: string): FilterOption<DateFilterValue>[] => {
  const today = dayjs();
  const startDate = today.subtract(6, 'day').startOf('day').format(DATE_FORMAT);
  const endDate = today.endOf('day').format(DATE_FORMAT);

  return [
    {
      label,
      value: {
        period: Period.SEVEN_DAYS,
        startDate,
        endDate,
      },
    },
  ];
};

export const buildDefaultMonth = (label: string): FilterOption<DateFilterValue>[] => {
  const today = dayjs();
  const startDate = today.subtract(29, 'day').startOf('day').format(DATE_FORMAT);
  const endDate = today.endOf('day').format(DATE_FORMAT);

  return [
    {
      label,
      value: {
        period: Period.THIRTY_DAYS,
        startDate,
        endDate,
      },
    },
  ];
};

export const getIntervalForPeriod = (
  periodValue: FilterOption<DateFilterValue>[] | null,
): AnalyticsDialogsRequest['interval'] => {
  const selected = periodValue?.[0]?.value;
  if (!selected?.startDate || !selected?.endDate) return 'day';

  const startDate = dayjs(selected.startDate);
  const endDate = dayjs(selected.endDate);
  if (!startDate.isValid() || !endDate.isValid()) return 'day';

  return startDate.isSame(endDate, 'day') ? 'hour' : 'day';
};
